import React, { useState, useRef, useCallback, useEffect } from 'react'

import { UploadOutlined, DownloadOutlined, InboxOutlined } from '@ant-design/icons'
import { Modal, Button, Upload, Select, Alert, message, Checkbox, Input } from 'antd'
import type { UploadFile, RcFile } from 'antd/es/upload/interface'
import { useTranslation } from 'react-i18next'
import { useOperatorData } from '@contexts/operator/OperatorDataContext'
import { useIsStandard } from '@contexts/operator/PlanContext'

const { Dragger } = Upload

interface MerchantOption {
  id: string;
  name: string;
  [key: string]: unknown;
}

interface ImportTypeOption {
  label: string;
  value: string;
  [key: string]: unknown;
}

interface ImportModalProps {
  open: boolean;
  onClose: () => void;
  // method 語法（bivariant）讓呼叫端可定義較窄的參數型別（e.g. file: File 而非 RcFile|UploadFile）
  // 而不需在 caller 加 cast。Modal 內部會傳 RcFile（runtime 上 RcFile extends File）。
  onImport(
    file: any,
    merchantId: string | null,
    overrideExisting: boolean,
    importType?: string | null,
    attachments?: any[],
    note?: string
  ): Promise<unknown>;
  role?: 'admin' | 'operator';
  merchants?: MerchantOption[];
  title?: string;
  onDownloadTemplate?: (merchantId?: string) => unknown;
  showOverrideExisting?: boolean;
  importTypeOptions?: ImportTypeOption[];
  defaultImportType?: string | null;
  importTypeLabel?: string | null;
  allowAttachments?: boolean;
  showNote?: boolean;
}

interface OperatorDataShape {
  merchant?: { id?: string };
  setCurrentMerchant?: (m: MerchantOption) => void;
  [key: string]: unknown;
}

/**
 * 通用匯入 Modal 組件
 */
const ImportModal: React.FC<ImportModalProps> = ({
  open,
  onClose,
  onImport,
  role = 'admin',
  merchants = [],
  title,
  onDownloadTemplate,
  showOverrideExisting = true,
  importTypeOptions = [],
  defaultImportType = null,
  importTypeLabel = null,
  allowAttachments = false,
  showNote = false,
}) => {
  const { t } = useTranslation()
  const operatorData = useOperatorData() as OperatorDataShape | null
  const isStandard = useIsStandard()
  const [selectedFile, setSelectedFile] = useState<RcFile | UploadFile | null>(null)
  const [selectedAttachments, setSelectedAttachments] = useState<(RcFile | UploadFile)[]>([])
  // Lazy initializer: read operatorData synchronously if it's already available at mount,
  // so the download-template button isn't spuriously disabled while waiting for the effect.
  const [selectedMerchantId, setSelectedMerchantId] = useState<string | null>(
    () => (role === 'operator' ? operatorData?.merchant?.id ?? null : null)
  )
  const [overrideExisting, setOverrideExisting] = useState(true)
  const [selectedImportType, setSelectedImportType] = useState<string | null>(
    defaultImportType || (importTypeOptions.length > 0 ? importTypeOptions[0].value : null)
  )
  const [note, setNote] = useState('')
  const [uploading, setUploading] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const downloadTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Sync with global default if provided (Operator only)
  useEffect(() => {
    if (open && role === 'operator' && operatorData?.merchant?.id) {
      setSelectedMerchantId(operatorData.merchant.id)
    }
  }, [open, role, operatorData?.merchant?.id])

  // Auto-select merchant if there is only one
  useEffect(() => {
    if (open && role === 'operator' && merchants.length === 1) {
      setSelectedMerchantId(merchants[0].id)
    }
  }, [open, role, merchants])

  // 檔案選擇前的驗證（主要匯入檔案）
  const beforeUpload = (file: RcFile): boolean => {
    // 驗證檔案類型
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
      'application/vnd.ms-excel' // .xls
    ]

    if (!validTypes.includes(file.type)) {
      message.error(t('common.import_modal.invalid_file') as string)
      return false
    }

    // 驗證檔案大小（限制 10MB）
    const isLt10M = file.size / 1024 / 1024 < 10
    if (!isLt10M) {
      message.error(t('common.import_modal.file_too_large') as string)
      return false
    }

    setSelectedFile(file)
    return false // 阻止自動上傳
  }

  // 附加檔案選擇前的驗證
  const beforeAttachmentUpload = (file: RcFile): boolean => {
    // 驗證檔案類型（JPEG, PNG, PDF）
    const validTypes = ['image/jpeg', 'image/png', 'application/pdf']
    if (!validTypes.includes(file.type)) {
      message.error(t('common.import_modal.invalid_attachment_type') as string)
      return false
    }

    // 驗證檔案大小（限制 10MB）
    const isLt10M = file.size / 1024 / 1024 < 10
    if (!isLt10M) {
      message.error(t('common.import_modal.file_too_large') as string)
      return false
    }

    // 確保檔案有 uid（Ant Design Upload 需要）
    if (!file.uid) {
      file.uid = `${Date.now()}-${Math.random()}`
    }

    setSelectedAttachments((prev) => [...prev, file])
    return false // 阻止自動上傳
  }

  // 處理上傳
  const handleUpload = async () => {
    if (!selectedFile) {
      message.warning(t('common.import_modal.no_file') as string)
      return
    }

    // Operator 必須選擇 Merchant
    if (role === 'operator' && !selectedMerchantId) {
      message.warning(t('common.import_modal.no_merchant_selected') as string)
      return
    }

    // 如果有匯入類型選項，必須選擇類型
    if (importTypeOptions.length > 0 && !selectedImportType) {
      message.warning(t('common.import_modal.no_import_type_selected') as string)
      return
    }

    try {
      setUploading(true)
      // 如果隱藏覆蓋選項，則使用預設值 false
      const overrideValue = showOverrideExisting ? overrideExisting : false
      await onImport(selectedFile, selectedMerchantId, overrideValue, selectedImportType, selectedAttachments, note)
      handleClose()
    } catch (error) {
      console.error('Upload error:', error)
    } finally {
      setUploading(false)
    }
  }

  // 關閉 Modal
  const handleClose = () => {
    setSelectedFile(null)
    setSelectedAttachments([])
    setSelectedMerchantId(null)
    setOverrideExisting(true)
    setSelectedImportType(
      defaultImportType || (importTypeOptions.length > 0 ? importTypeOptions[0].value : null)
    )
    setNote('')
    setUploading(false)
    onClose && onClose()
  }

  // 下載範例（帶 debounce）
  const handleDownloadTemplate = useCallback(() => {
    // 如果正在下載，忽略點擊
    if (downloading) {
      return
    }

    // Operator 必須選擇 Merchant
    if (role === 'operator' && !selectedMerchantId) {
      message.warning(t('common.import_modal.no_merchant_selected') as string)
      return
    }

    // 清除之前的 timer
    if (downloadTimeoutRef.current) {
      clearTimeout(downloadTimeoutRef.current)
    }

    // 設定 debounce（300ms）
    downloadTimeoutRef.current = setTimeout(() => {
      setDownloading(true)
      try {
        if (onDownloadTemplate) {
          // For operator, pass merchantId; for admin, pass undefined (uses current merchant from context)
          onDownloadTemplate(role === 'operator' ? (selectedMerchantId ?? undefined) : undefined)
        } else {
          message.info(t('common.import_modal.template_not_available') as string)
        }
      } finally {
        // 延遲重置下載狀態，避免快速連續點擊
        setTimeout(() => {
          setDownloading(false)
        }, 1000)
      }
    }, 300)
  }, [onDownloadTemplate, downloading, t, role, selectedMerchantId])

  // 清理 debounce timer
  useEffect(() => {
    return () => {
      if (downloadTimeoutRef.current) {
        clearTimeout(downloadTimeoutRef.current)
      }
    }
  }, [])

  return (
    <Modal
      centered
      open={open}
      onCancel={handleClose}
      footer={null}
      width={600}
      destroyOnHidden
      title={title || (t('common.import_modal.title') as string)}
    >
      <div style={{ padding: '16px 0' }}>
        {/* 說明提示 */}
        <Alert
          message={t('common.import_modal.description') as string}
          type="info"
          showIcon
          style={{ marginBottom: '24px' }}
        />

        {/* 匯入類型選擇器 */}
        {importTypeOptions.length > 0 && (
          <div style={{ marginBottom: '24px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>
              {importTypeLabel || (t('common.import_modal.select_import_type') as string)} <span style={{ color: '#ff4d4f' }}>*</span>
            </label>
            <Select
              placeholder={t('common.import_modal.select_import_type_placeholder') as string}
              value={selectedImportType}
              onChange={setSelectedImportType}
              style={{ width: '100%' }}
              size="large"
              options={importTypeOptions}
            />
          </div>
        )}

        {/* Admin: 下載範例按鈕（在匯入類型選擇器之後） */}
        {role === 'admin' && (
          <div style={{ marginBottom: '24px' }}>
            <Button
              icon={<DownloadOutlined />}
              onClick={handleDownloadTemplate}
              block
              loading={downloading}
              disabled={downloading}
            >
              {t('common.import_modal.download_template') as string}
            </Button>
          </div>
        )}

        {/* Operator: Merchant 選擇器（single-merchant 模式隱藏） */}
        {role === 'operator' && !isStandard && (
          <div style={{ marginBottom: '24px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>
              {t('common.import_modal.select_merchant') as string} <span style={{ color: '#ff4d4f' }}>*</span>
            </label>
            <Select
              placeholder={t('common.import_modal.select_merchant_placeholder') as string}
              value={selectedMerchantId}
              onChange={(val: string) => {
                setSelectedMerchantId(val)
                // Sync to global state (Operator only)
                if (role === 'operator' && operatorData?.setCurrentMerchant) {
                  const merchant = merchants.find((m) => m.id === val)
                  if (merchant) operatorData.setCurrentMerchant(merchant)
                }
              }}
              style={{ width: '100%' }}
              size="large"
              showSearch
              optionFilterProp="label"
              options={merchants.map((merchant) => ({
                label: merchant.name,
                value: merchant.id
              }))}
            />
          </div>
        )}

        {/* Operator: 下載範例按鈕（在 Merchant 選擇器之後） */}
        {role === 'operator' && (
          <div style={{ marginBottom: '24px' }}>
            <Button
              icon={<DownloadOutlined />}
              onClick={handleDownloadTemplate}
              block
              loading={downloading}
              disabled={downloading || !operatorData?.merchant?.id}
            >
              {t('common.import_modal.download_template') as string}
            </Button>
          </div>
        )}

        {/* 檔案上傳區域 */}
        <div style={{ marginBottom: '24px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>
            {t('common.import_modal.select_file') as string} <span style={{ color: '#ff4d4f' }}>*</span>
          </label>
          <Dragger
            accept=".xlsx,.xls"
            beforeUpload={beforeUpload}
            onRemove={() => setSelectedFile(null)}
            fileList={selectedFile ? [selectedFile as UploadFile] : []}
            maxCount={1}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">
              {t('common.import_modal.drag_or_click') as string}
            </p>
            <p className="ant-upload-hint">
              {t('common.import_modal.file_format_hint') as string}
            </p>
          </Dragger>
        </div>

        {/* 附加檔案上傳區域（僅當 allowAttachments 為 true 時顯示） */}
        {allowAttachments && (
          <div style={{ marginBottom: '24px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>
              {t('common.import_modal.attachments') as string} <span style={{ color: '#999', fontSize: '12px' }}>({t('common.import_modal.attachments_optional') as string})</span>
            </label>
            <Dragger
              accept=".jpg,.jpeg,.png,.pdf"
              beforeUpload={beforeAttachmentUpload}
              onRemove={(file) => {
                setSelectedAttachments((prev) => prev.filter((f) => (f as UploadFile).uid !== (file as UploadFile).uid))
              }}
              fileList={selectedAttachments as UploadFile[]}
              multiple
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">
                {t('common.import_modal.drag_or_click_attachments') as string}
              </p>
              <p className="ant-upload-hint">
                {t('common.import_modal.attachments_format_hint') as string}
              </p>
            </Dragger>
          </div>
        )}

        {/* Note 輸入區 */}
        {showNote && (
          <div style={{ marginBottom: '24px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>
              {t('common.note') as string}
            </label>
            <Input
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>
        )}

        {/* 覆蓋現有資料選項 */}
        {showOverrideExisting && (
          <div style={{ marginBottom: '24px' }}>
            <Checkbox
              checked={overrideExisting}
              onChange={(e) => setOverrideExisting(e.target.checked)}
            >
              {t('common.import_modal.override_existing') as string}
            </Checkbox>
          </div>
        )}

        {/* 按鈕區域 */}
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <Button onClick={handleClose} disabled={uploading}>
            {t('common.button.cancel') as string}
          </Button>
          <Button
            type="primary"
            icon={<UploadOutlined />}
            onClick={handleUpload}
            disabled={
              !selectedFile ||
              (role === 'operator' && !selectedMerchantId) ||
              (importTypeOptions.length > 0 && !selectedImportType)
            }
            loading={uploading}
          >
            {t('common.import_modal.upload') as string}
          </Button>
        </div>
      </div>
    </Modal>
  )
}

export default ImportModal
